/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { Box } from "@mui/material";
import { environment } from "../../environment";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nBasis";
import CommandBar from "../CommandBar";
import CommandBarSearchInput from "../CommandBarSearchInput";
import InnerView from "../InnerView";
import LoadingIndicator from "../LoadingIndicator";
import ViewHeader from "../ViewHeader";
import NavigationViewProps from "./NavigationViewProps";
import variablesScss from "../../Themes/variables.module.scss";

export default function NavigationView(props: NavigationViewProps) {
    const { t } = useTranslation();
    const color = environment.istBuchungsportal ? "primary.main" : "secondary.main";

    return (
        <InnerView>
            <ViewHeader title={props.model.titel} hideCloseButton />

            {!props.versteckeSuche && <CommandBar sx={{ borderBottom: "none" }}>
                <CommandBarSearchInput onSearch={props.model.findeMitLaden} variant="navigation" />
            </CommandBar>}

            <LoadingIndicator loading={props.model.loading}>
                <Box
                    role="listbox"
                    sx={{
                        flexGrow: 1,
                        margin: "0.5rem",
                        marginTop: 0,
                        border: `1px solid ${variablesScss.colorsDefaultBorder}`,
                        overflowX: "hidden",
                    }}
                >
                    {props.model.links.map((link, idx) => (
                        <Box
                            key={idx}
                            sx={[
                                {
                                    padding: "0.875rem",
                                    paddingLeft: "0.5rem",
                                    fontSize: "0.875rem",
                                    textAlign: "left",
                                    fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
                                    fontWeight: 400,
                                    lineHeight: 1.43,
                                    letterSpacing: "0.01071em",
                                    verticalAlign: "inherit",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    gap: "1ch",
                                    maxHeight: "3rem",
                                    "&:hover": {
                                        backgroundColor: "action.hover",
                                    }
                                },
                                link.istAusgewaehlt && {
                                    backgroundColor: "action.selected",
                                }
                            ]}
                            onClick={props.model.navigate(
                                link.url,
                                link.istAusgewaehlt
                            )}
                            role="option"
                            aria-selected={link.istAusgewaehlt}
                            aria-label={t(translationKeys.navigationView.row, { url: link.url })}
                        >
                            {link.icon}
                            <Box
                                className="lan-navigationview__link-clarity"
                                component="span"
                                title={link.bezeichnung}
                                sx={{
                                    color: color,
                                    flex: 1,
                                    whiteSpace: "nowrap",
                                    overflowX: "hidden",
                                    textOverflow: "ellipsis",
                                    "&:hover": {
                                        textDecoration: "underline",
                                    }
                                }}
                            >
                                {link.bezeichnung}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </LoadingIndicator>
        </InnerView>
    );
}
